/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosProgressEvent } from 'axios';
import { getApi } from '../common/requestHelper';
import {
  CreateExternalGlossaryCollectionResponseDtoStatusEnum,
  CreateExternalGlossaryDto,
  CreateGlossaryDto,
  CreateGlossaryItemDto,
  GlossaryApi,
  GlossaryCollectionDto,
  GlossaryItemDto,
  GlossaryItemResponseDto,
  UpdateGlossaryItemDto,
} from '@ink-ai/insight-service-sdk';
import Papa from 'papaparse';
import { app } from './app';
import { RootState } from '.';
import { downloadFromLink } from '../common/utils';

export type GlossaryItemTemplateDto = Omit<
  GlossaryItemResponseDto,
  'id' | 'uuid' | 'parentId' | 'createdAt' | 'updatedAt'
>;

export enum ImportFileParseStatus {
  WaitingUpload = 'Waiting upload',
  Complete = 'Complete',
  Error = 'Error',
  Uploading = 'Uploading',
  Importing = 'Importing',
}

export const statusStringMapping: Record<
  CreateExternalGlossaryCollectionResponseDtoStatusEnum,
  string
> = {
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.PendingUpload]:
    'Pending Upload',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Importing]:
    'Importing',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Ready]: 'Ready',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Error]: 'Failed',
};

export const statusClassMapping: Record<
  CreateExternalGlossaryCollectionResponseDtoStatusEnum,
  string
> = {
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.PendingUpload]:
    'status-info',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Importing]:
    'status-info',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Ready]:
    'status-success',
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Error]: 'status-error',
};

export type GlossaryState = {
  glossaries: GlossaryCollectionDto[];
  glossaryItems: GlossaryItemResponseDto[];
  loading: boolean;
  selectedGlossary: GlossaryCollectionDto | null;
  importFileParseStatus: ImportFileParseStatus | null;
  parsedFileContent: GlossaryItemTemplateDto[] | null;
  timestamp: number; // Used to tell the action whether the job is outdated
  progress: number;
  glossaryItemsTotal: number;
  glossaryItemsPage: number;
  glossaryItemsPageSize: number;
  glossaryItemsTotalPages: number;
};

const initialState: GlossaryState = {
  glossaries: [],
  glossaryItems: [],
  loading: false,
  selectedGlossary: null,
  importFileParseStatus: ImportFileParseStatus.WaitingUpload,
  parsedFileContent: null,
  timestamp: 0,
  progress: 0,
  glossaryItemsTotal: 0,
  glossaryItemsPage: 1,
  glossaryItemsPageSize: 20,
  glossaryItemsTotalPages: 0,
};

export const handleDownload = async (id: string, suggestedName?: string) => {
  try {
    const glossaryApi = await getApi(GlossaryApi);
    const response = await glossaryApi.getGlossaryDownloadLink(id);
    await downloadFromLink(response.data.downloadLink, suggestedName);
  } catch (error: any) {
    console.error('Failed to download the glossary:', error);
  }
};

export const listGlossaryCollections = createAsyncThunk<
  GlossaryCollectionDto[]
>('glossary/listGlossaryCollections', async () => {
  const glossaryApi = await getApi(GlossaryApi);
  const response =
    await glossaryApi.glossaryControllerListGlossaryCollections();
  return response.data.collections;
});

export const listGlossaryItemsById = createAsyncThunk(
  'glossary/listGlossaryItemsById', 
  async ({ uuid, page = '1', pageSize = '20', searchKeyword }: { 
    uuid: string; 
    page?: string; 
    pageSize?: string; 
    searchKeyword?: string; 
  }) => {
    const glossaryApi = await getApi(GlossaryApi);
    const response =
    await glossaryApi.glossaryControllerListGlossaryItemsById(uuid, page, pageSize, searchKeyword || '');
    return {
      items: response.data.items,
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalPages: response.data.totalPages,
    };
  },
);

export const getGlossaryById = createAsyncThunk(
  'glossary/getGlossaryById',
  async (uuid: string) => {
    const glossaryApi = await getApi(GlossaryApi);
    const response = await glossaryApi.getGlossaryById(uuid);
    return response.data;
  },
);

export const createGlossaryItem = createAsyncThunk(
  'glossary/createGlossaryItem',
  async (
    {
      glossaryUuid,
      item,
    }: { glossaryUuid: string; item: CreateGlossaryItemDto },
    { dispatch },
  ) => {
    const glossaryApi = await getApi(GlossaryApi);
    await glossaryApi.glossaryControllerCreateGlossaryItem(glossaryUuid, item);
    dispatch(listGlossaryItemsById({ 
      uuid: glossaryUuid, page: '1', pageSize: '10', searchKeyword: undefined }));
  },
);

export const updateGlossaryItem = createAsyncThunk(
  'glossary/updateGlossaryItem',
  async (
    {
      glossaryUuid,
      itemUuid,
      item,
    }: {
      glossaryUuid: string;
      itemUuid: string;
      item: UpdateGlossaryItemDto;
    },
    { dispatch },
  ) => {
    const glossaryApi = await getApi(GlossaryApi);
    const response = await glossaryApi.glossaryControllerUpdateGlossaryItem(
      glossaryUuid,
      itemUuid,
      item,
    );
    dispatch(listGlossaryItemsById({ 
      uuid: glossaryUuid, 
      page: '1', 
      pageSize: '10', 
      searchKeyword: undefined 
    }));
    return response.data;
  },
);

export const removeGlossaryItems = createAsyncThunk(
  'glossary/removeGlossaryItems',
  async (
    { glossaryUuid, itemUuids }: { glossaryUuid: string; itemUuids: string[] },
    { getState, dispatch },
  ) => {
    const state = getState() as { glossary: GlossaryState };
    const glossaryApi = await getApi(GlossaryApi);

    const deletePromises = itemUuids.map(async (itemUuid) => {
      const item = state.glossary.glossaryItems.find(
        (g) => g.uuid === itemUuid,
      );
      if (item) {
        await glossaryApi.glossaryControllerRemoveGlossaryItem(
          glossaryUuid,
          item.uuid,
        );
      }
    });

    await Promise.all(deletePromises);
    dispatch(listGlossaryItemsById({ 
      uuid: glossaryUuid, 
      page: '1', 
      pageSize: '10', 
      searchKeyword: undefined 
    }));
  },
);

export const deleteGlossaryCollections = createAsyncThunk(
  'glossary/deleteGlossaryCollections',
  async (glossaryIds: string[], { getState, dispatch }) => {
    console.log('[GlossaryThunk] deleteGlossaryCollections start', { count: glossaryIds?.length ?? 0, ids: glossaryIds });
    const state = getState() as { glossary: GlossaryState };
    const glossaryApi = await getApi(GlossaryApi);

    // 直接按传入的 ID 执行删除，避免依赖 state.glossaries 匹配导致跳过
    const deletePromises = glossaryIds.map(async (uuid) => {
      try {
        console.log('[GlossaryThunk] deleting glossary collection', { id: uuid });
        await glossaryApi.glossaryControllerDeleteGlossaryCollection(uuid);
      } catch (e: any) {
        console.error('[GlossaryThunk] deleteGlossaryCollections failed for id', { id: uuid, error: e?.message || e });
      }
    });

    await Promise.all(deletePromises);
    console.log('[GlossaryThunk] deleteGlossaryCollections completed');
    dispatch(listGlossaryCollections());
  },
);

export const createGlossaryFromParsedFile = createAsyncThunk(
  'glossary/createGlossaryFromParsedFile',
  async (
    { name, description }: { name: string; description: string },
    { getState, dispatch },
  ) => {
    try {
      const state = getState() as { glossary: GlossaryState };
      const parsedFileContent = state.glossary.parsedFileContent;

      if (!parsedFileContent) {
        throw new Error('No parsed file content available');
      }

      const items: GlossaryItemDto[] = parsedFileContent.map((item: any) => {
        const { translations, ...rest } = item;
        return {
          ...rest,
          trans: translations,
        };
      });

      const newGlossary: CreateGlossaryDto = {
        name,
        desc: description,
        items: items,
      };

      const glossaryApi = await getApi(GlossaryApi);
      await glossaryApi.glossaryControllerCreateGlossaryCollection(newGlossary);
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Glossary imported successfully.',
          status: 'success',
        }),
      );
      dispatch(app.actions.setNavigationPath('/glossary-management'));
    } catch (error: any) {
      dispatch(
        app.actions.setGlobalMessage({
          message:
            error.message || 'Failed to create glossary from parsed file',
          status: 'error',
        }),
      );
      throw new Error(
        error.message || 'Failed to create glossary from parsed file',
      );
    }
  },
);

export const importExternalGlossary = createAsyncThunk(
  'glossary/importExternalGlossary',
  async (
    {
      name,
      description,
      file,
      uuid,
    }: { name: string; description: string; file: File; uuid?: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState() as RootState;
    const initTimestamp = state.glossary.timestamp;
    // Get the presigned URL
    dispatch(
      glossaryActions.setImportFileParseStatus({
        status: ImportFileParseStatus.Uploading,
        content: null,
      }),
    );
    const glossaryApi = await getApi(GlossaryApi);
    let response;
    try {
      if (uuid) {
        const newExternalGlossary: CreateExternalGlossaryDto = {
          name: name,
          desc: description,
        };
        response = await glossaryApi.updateForUploadGlossary(
          uuid,
          newExternalGlossary,
        );
      } else {
        response = await glossaryApi.createForUploadGlossary({
          name,
          desc: description,
        });
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Import request failed';
      // 回退上传状态，避免前端停留在上传态
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.WaitingUpload,
          content: null,
        }),
      );
      return rejectWithValue({ status: err?.response?.status, message });
    }
    const { uploadUrl, uuid: responseUuid } = response.data;

    // Upload the file to S3
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          const currState = getState() as RootState;
          if (initTimestamp !== currState.glossary.timestamp) {
            return;
          }
          dispatch(glossaryActions.setProgress(progress));
        },
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Error uploading file to S3';
      // 上传失败则直接中止流程并返回错误
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.WaitingUpload,
          content: null,
        }),
      );
      return rejectWithValue({ status: error?.response?.status, message });
    }

    // Start the ingestion
    const currState = getState() as RootState;
    if (initTimestamp !== currState.glossary.timestamp) {
      console.warn('The job is outdated, aborting');
      return;
    }
    dispatch(
      glossaryActions.setImportFileParseStatus({
        status: ImportFileParseStatus.Importing,
        content: null,
      }),
    ),
      await glossaryApi.startImport(responseUuid);
    dispatch(app.actions.setNavigationPath('/glossary-management'));
  },
);

export const importInternalGlossary = createAsyncThunk(
  'glossary/importInternalGlossary',
  async (
    {
      name,
      description,
      file,
      uuid,
    }: { name: string; description: string; file: File; uuid?: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState() as RootState;
    const initTimestamp = state.glossary.timestamp;
    // Get the presigned URL
    dispatch(
      glossaryActions.setImportFileParseStatus({
        status: ImportFileParseStatus.Uploading,
        content: null,
      }),
    );
    const glossaryApi = await getApi(GlossaryApi);
    let response;
    try {
      if (uuid) {
        const newInternalGlossary: CreateExternalGlossaryDto = {
          name: name,
          desc: description,
        };
        response = await glossaryApi.updateInternalForUploadGlossary(
          uuid,
          newInternalGlossary,
        );
      } else {
        response = await glossaryApi.createInternalForUploadGlossary({
          name,
          desc: description,
        });
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Import request failed';
      // 回退上传状态，避免前端停留在上传态
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.WaitingUpload,
          content: null,
        }),
      );
      return rejectWithValue({ status: err?.response?.status, message });
    }
    const { uploadUrl, uuid: responseUuid } = response.data;

    // Upload the file to S3
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          const currState = getState() as RootState;
          if (initTimestamp !== currState.glossary.timestamp) {
            return;
          }
          dispatch(glossaryActions.setProgress(progress));
        },
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Error uploading file to S3';
      // 上传失败则直接中止流程并返回错误
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.WaitingUpload,
          content: null,
        }),
      );
      return rejectWithValue({ status: error?.response?.status, message });
    }

    // Start the ingestion
    const currState = getState() as RootState;
    if (initTimestamp !== currState.glossary.timestamp) {
      console.warn('The job is outdated, aborting');
      return;
    }
    dispatch(
      glossaryActions.setImportFileParseStatus({
        status: ImportFileParseStatus.Importing,
        content: null,
      }),
    ),
      await glossaryApi.startInternalImport(responseUuid);
    dispatch(app.actions.setNavigationPath('/glossary-management'));
  },
);

export const updateGlossaryFromParsedFile = createAsyncThunk(
  'glossary/updateGlossaryFromParsedFile',
  async (
    {
      glossaryUuid,
      name,
      description,
    }: { glossaryUuid: string; name: string; description: string },
    { getState, dispatch },
  ) => {
    try {
      const state = getState() as { glossary: GlossaryState };
      const parsedFileContent = state.glossary.parsedFileContent;

      if (!parsedFileContent) {
        throw new Error('No parsed file content available');
      }

      const items: GlossaryItemDto[] = parsedFileContent.map((item) => {
        const { translations, ...rest } = item;
        return {
          ...rest,
          trans: translations,
        };
      });

      const newGlossary: CreateGlossaryDto = {
        name: name,
        desc: description,
        items: items,
      };

      const glossaryApi = await getApi(GlossaryApi);
      await glossaryApi.glossaryControllerUpdateGlossaryCollection(
        glossaryUuid,
        newGlossary,
      );

      dispatch(
        app.actions.setGlobalMessage({
          message: 'Glossary updated successfully.',
          status: 'success',
        }),
      );
      dispatch(app.actions.setNavigationPath('/glossary-management'));
      return newGlossary;
    } catch (error: any) {
      dispatch(
        app.actions.setGlobalMessage({
          message:
            error.message || 'Failed to update glossary from parsed file',
          status: 'error',
        }),
      );
      throw error;
    }
  },
);

export const exportGlossaryItemsToCsv = createAsyncThunk(
  'glossary/exportGlossaryItemsToCsv',
  async (glossaryId: string, { getState }) => {
    const state = getState() as { glossary: GlossaryState };
    const selectedGlossary = state.glossary.glossaries.find(
      (g) => g.uuid === glossaryId,
    );
    if (selectedGlossary) {
      const glossaryApi = await getApi(GlossaryApi);
      const response =
        await glossaryApi.glossaryControllerListGlossaryItemsById(
          selectedGlossary.uuid,
          '1',
          '2000',
          ''
        );
      const glossaryItems = response.data.items;

      const exportData = glossaryItems.map((item) => {
        const { name, desc, abbr, translations } = item;
        const translationColumns = translations.reduce((acc, translate) => {
          acc[translate.language] = translate.translation;
          return acc;
        }, {});
        return { name, desc, abbr, ...translationColumns };
      });

      const csvHeaders = [
        'name',
        'desc',
        'abbr',
        ...Array.from(
          new Set(
            glossaryItems.flatMap((item) =>
              item.translations.map((translate) => translate.language),
            ),
          ),
        ),
      ];

      const csvData = [
        csvHeaders,
        ...exportData.map((row) =>
          csvHeaders.map((header) => row[header] || ''),
        ),
      ];

      const csv = Papa.unparse(csvData, {
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        newline: '\n',
      });

      const downloadExportCsv = () => {
        const element = document.createElement('a');
        const file = new Blob(['\uFEFF' + csv], {
          type: 'text/csv;charset=utf-8;',
        });
        element.href = URL.createObjectURL(file);
        element.download = `${selectedGlossary.name}.csv`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      };
      downloadExportCsv();
    }
  },
);

export const exportGlossaryItemsToJSON = createAsyncThunk(
  'glossary/exportGlossaryItemsToJSON',
  async (glossaryId: string, { getState }) => {
    const state = getState() as { glossary: GlossaryState };
    const selectedGlossary = state.glossary.glossaries.find(
      (g) => g.uuid === glossaryId,
    );
    if (selectedGlossary) {
      const glossaryApi = await getApi(GlossaryApi);
      const response =
        await glossaryApi.glossaryControllerListGlossaryItemsById(
          selectedGlossary.uuid,
          '1',
          '2000',
          ''
        );
      const exportData = response.data.items.map((item) => {
        const { name, desc, abbr, translations } = item;
        const filteredTranslations = translations.map((translate) => ({
          language: translate.language,
          translation: translate.translation,
        }));
        return { name, desc, abbr, translations: filteredTranslations };
      });

      const downloadExportJson = () => {
        const element = document.createElement('a');
        const file = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json',
        });
        element.href = URL.createObjectURL(file);
        element.download = `${selectedGlossary.name}.json`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      };
      downloadExportJson();
    }
  },
);

export const glossary = createSlice({
  name: 'glossary',
  initialState,
  reducers: {
    setLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
      console.log('state.loading: ', state.loading);
    },
    setSelectedGlossary(
      state,
      { payload }: PayloadAction<GlossaryCollectionDto | null>,
    ) {
      state.selectedGlossary = payload;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
    },
    setImportFileParseStatus(
      state,
      {
        payload,
      }: PayloadAction<{
        status: ImportFileParseStatus | null;
        content: GlossaryItemTemplateDto[] | null;
      }>,
    ) {
      state.importFileParseStatus = payload.status;
      state.parsedFileContent = payload.content;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listGlossaryCollections.pending, (state) => {
        state.loading = true;
      })
      .addCase(listGlossaryCollections.fulfilled, (state, action) => {
        state.glossaries = action.payload;
        state.loading = false;
      })
      .addCase(listGlossaryCollections.rejected, (state) => {
        state.loading = false;
      })
      .addCase(listGlossaryItemsById.pending, (state) => {
        state.loading = true;
      })
      .addCase(listGlossaryItemsById.fulfilled, (state, action) => {
        state.glossaryItems = action.payload.items;
        state.glossaryItemsTotal = action.payload.total;
        state.glossaryItemsPage = action.payload.page;
        state.glossaryItemsPageSize = action.payload.pageSize;
        state.glossaryItemsTotalPages = action.payload.totalPages;
        state.loading = false;
      })
      .addCase(listGlossaryItemsById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getGlossaryById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGlossaryById.fulfilled, (state, action) => {
        state.selectedGlossary = action.payload;
        state.loading = false;
      })
      .addCase(getGlossaryById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateGlossaryItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateGlossaryItem.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateGlossaryItem.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createGlossaryFromParsedFile.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGlossaryFromParsedFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createGlossaryFromParsedFile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(importExternalGlossary.pending, (state) => {
        state.loading = true;
      })
      .addCase(importExternalGlossary.fulfilled, (state) => {
        state.loading = false;
        state.progress = 0;
      })
      .addCase(importExternalGlossary.rejected, (state) => {
        state.loading = false;
        state.progress = 0;
      })
      .addCase(importInternalGlossary.pending, (state) => {
        state.loading = true;
      })
      .addCase(importInternalGlossary.fulfilled, (state) => {
        state.loading = false;
        state.progress = 0;
      })
      .addCase(importInternalGlossary.rejected, (state) => {
        state.loading = false;
        state.progress = 0;
      });
  },
});

export const glossaryActions = glossary.actions;
export default glossary.reducer;
